/**
 * LandingImpactCard — Phase Z.
 *
 * Renders ONLY when the visitor is an authenticated user AND has at
 * least one Impact-Clock prompt due within 30 days.
 *
 *   • Anonymous visitors  → null
 *   • Empty queue         → null (deliberately no empty-state UI)
 *   • ≥ 1 prompt within 30 days → card renders below hero
 *
 * The card is pure v5 palette — white surface, indigo accents, no
 * animations that would compete with hero motion.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Clock } from "lucide-react";
import { useAuth } from "../../lib/AuthContext";
import { useReducedMotion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function daysUntil(dateStr) {
    if (!dateStr) return Infinity;
    const t = Date.parse(dateStr);
    if (Number.isNaN(t)) return Infinity;
    return Math.round((t - Date.now()) / MS_PER_DAY);
}

export default function LandingImpactCard() {
    const { user } = useAuth() || {};
    const isAuthenticated = !!(user && user.user_id);
    const reduce = useReducedMotion();
    const [prompts, setPrompts] = useState(null);
    const [howOpen, setHowOpen] = useState(false);
    const dialogRef = useRef(null);

    // Only fetch for authenticated users.
    useEffect(() => {
        if (!isAuthenticated) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${BACKEND_URL}/api/impact-clock`, { credentials: "include" });
                if (!res.ok) return;
                const j = await res.json();
                if (!cancelled) setPrompts(j?.prompts || []);
            } catch { /* silent — component just doesn't render */ }
        })();
        return () => { cancelled = true; };
    }, [isAuthenticated, user?.user_id]);

    const upcoming = useMemo(() => {
        if (!Array.isArray(prompts)) return [];
        return prompts
            .filter((p) => daysUntil(p.due_date || p.dueAt || p.due) <= 30)
            .slice(0, 12);
    }, [prompts]);

    // Escape closes dialog.
    useEffect(() => {
        if (!howOpen) return;
        const onKey = (e) => { if (e.key === "Escape") setHowOpen(false); };
        window.addEventListener("keydown", onKey);
        // Focus into dialog on open.
        setTimeout(() => dialogRef.current?.focus?.(), 20);
        return () => window.removeEventListener("keydown", onKey);
    }, [howOpen]);

    // Render nothing if unauthenticated or empty queue.
    if (!isAuthenticated) return null;
    if (!Array.isArray(prompts)) return null;    // still loading
    if (upcoming.length === 0) return null;

    const nextThree = upcoming.slice(0, 3);
    const countLabel = upcoming.length === 1 ? "one outcome" : `${wordCount(upcoming.length)} outcomes`;

    return (
        <section className="bg-surface py-14" data-testid="landing-impact-card-section">
            <div className="max-w-wide mx-auto px-6 sm:px-10">
                <div
                    className="relative bg-surface rounded-xl px-7 py-6"
                    style={{
                        border: "0.5px solid var(--border, #E5E7EB)",
                        boxShadow: "0 4px 20px rgba(26, 35, 126, 0.06)",
                    }}
                    data-testid="landing-impact-card"
                >
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <div className="text-[11px] uppercase tracking-widest font-semibold text-ink-2">
                            Your calibration queue
                        </div>
                        <button
                            type="button"
                            onClick={() => setHowOpen(true)}
                            data-testid="landing-impact-how"
                            className="text-[11px] uppercase tracking-wider font-semibold text-brand hover:underline"
                        >
                            How this works
                        </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-[auto,1fr,auto] items-center gap-6">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-lg bg-brand-light flex items-center justify-center text-brand">
                                <Clock size={22} strokeWidth={1.6} />
                            </div>
                            <div>
                                <div
                                    className="font-heading text-[36px] font-bold text-ink-1 leading-none tabular-nums"
                                    data-testid="landing-impact-count"
                                >
                                    {upcoming.length}
                                </div>
                                <div className="text-[12px] text-ink-2 mt-1">
                                    due within the next thirty days
                                </div>
                            </div>
                        </div>

                        <div className="min-w-0" data-testid="landing-impact-timeline">
                            <div className="text-[13px] text-ink-1 leading-snug mb-2">
                                {countLabel} are ready for you to validate. Each one improves the
                                calibration engine for every user.
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {nextThree.map((p, i) => (
                                    <span
                                        key={`${p.decision_id || p.id || i}`}
                                        className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] font-mono uppercase tracking-wider"
                                        style={{ background: "#EEF0F7", color: "#1A237E", border: "0.5px solid #C5CAE9" }}
                                        data-testid={`landing-impact-pill-${i}`}
                                    >
                                        <span
                                            aria-hidden
                                            className="w-1.5 h-1.5 rounded-full"
                                            style={{ background: "#1A237E" }}
                                        />
                                        {shortDomain(p)} · {relativeDue(p)}
                                    </span>
                                ))}
                                {upcoming.length > 3 && (
                                    <span className="text-[11px] uppercase tracking-wider text-ink-2 self-center">
                                        + {upcoming.length - 3} more
                                    </span>
                                )}
                            </div>
                        </div>

                        <Link
                            to="/impact-clock"
                            data-testid="landing-impact-cta"
                            className="group inline-flex items-center gap-2 h-10 px-5 bg-brand text-white rounded-md font-semibold text-[13px] transition-all
                                       hover:bg-brand-dark hover:-translate-y-0.5 shadow-md hover:shadow-lg"
                        >
                            Open impact clock
                            <ChevronRight
                                size={14}
                                className={reduce ? "" : "transition-transform duration-200 group-hover:translate-x-0.5"}
                            />
                        </Link>
                    </div>
                </div>
            </div>

            {howOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="How the calibration loop works"
                    data-testid="landing-impact-how-dialog"
                    className="fixed inset-0 z-50 flex items-center justify-center px-6"
                    onClick={() => setHowOpen(false)}
                >
                    <div className="absolute inset-0" style={{ background: "rgba(15, 22, 41, 0.5)" }} />
                    <div
                        ref={dialogRef}
                        tabIndex={-1}
                        onClick={(e) => e.stopPropagation()}
                        className="relative bg-surface rounded-xl px-7 py-6 max-w-[480px] w-full"
                        style={{ border: "0.5px solid var(--border, #E5E7EB)", boxShadow: "0 12px 40px rgba(15,22,41,0.25)" }}
                    >
                        <div className="text-[11px] uppercase tracking-widest font-semibold text-ink-2 mb-2">
                            The calibration loop
                        </div>
                        <div className="font-heading text-[22px] font-semibold text-ink-1 mb-3 leading-snug">
                            Why validating outcomes matters.
                        </div>
                        <p className="text-[13.5px] text-ink-1 leading-relaxed mb-2">
                            Every time you record whether a modelled consequence actually
                            happened, the engine's archetype priors move a little closer to
                            reality. That drift compounds.
                        </p>
                        <p className="text-[13.5px] text-ink-2 leading-relaxed">
                            The direction-accuracy number on the landing page — the one you
                            saw when you signed up — is the read-out of that loop. It goes
                            up when validated outcomes match the engine's scenario range. It goes
                            down when they don't. Nothing else moves that dial.
                        </p>
                        <div className="mt-5 flex justify-end">
                            <button
                                type="button"
                                onClick={() => setHowOpen(false)}
                                data-testid="landing-impact-how-close"
                                className="inline-flex items-center gap-2 h-9 px-4 bg-brand text-white rounded-md font-semibold text-[12px] hover:bg-brand-dark transition-colors"
                            >
                                Got it
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

function shortDomain(p) {
    const s = p.domain_label || p.domain || p.archetype_id || p.title || "domain";
    return String(s).slice(0, 22);
}

function relativeDue(p) {
    const d = daysUntil(p.due_date || p.dueAt || p.due);
    if (d < 0)  return "overdue";
    if (d === 0) return "today";
    if (d === 1) return "tomorrow";
    if (d <= 7)  return `${d}d`;
    return `${d}d`;
}

function wordCount(n) {
    const w = ["zero","one","two","three","four","five","six","seven","eight","nine","ten","eleven","twelve"];
    return w[n] || `${n}`;
}
